import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppNav from "./AppNav";
import "./BookListPages.css";
import {
  cacheBookList,
  flushPendingBookListUpdates,
  getCachedBookList
} from "../utils/bookListCache";

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString.replace(" ", "T") + "Z").toLocaleDateString("en-GB");
}

function BookmarksPage() {
  const navigate = useNavigate();
  const user = useMemo(
    () => JSON.parse(localStorage.getItem("user") || "{}"),
    []
  );
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [swipedBookId, setSwipedBookId] = useState(null);
  const swipeStateRef = useRef({
    id: null,
    startX: 0,
    startY: 0,
    isSwiping: false
  });
  const suppressOpenRef = useRef(false);

  const fetchBooks = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const userId = user?.id || 0;

    if (!apiUrl || !userId) {
      setBooks([]);
      setLoading(false);
      return;
    }

    const cachedBooks = getCachedBookList(userId, "bookmarks");
    if (cachedBooks.length) {
      setBooks(cachedBooks);
      setLoading(false);
    }

    if (!navigator.onLine) return;

    try {
      await flushPendingBookListUpdates(apiUrl);
      const response = await fetch(`${apiUrl}/get-book-list.php?uid=${userId}&list=bookmarks`, {
        credentials: "include",
        cache: "no-store"
      });
      const data = await response.json();
      if (data?.success) {
        setBooks(data.items || []);
        cacheBookList(userId, "bookmarks", data.items || []);
      }
    } catch (error) {
      console.error("Failed to load bookmarks:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchBooks();
    document.body.style.backgroundColor = "#ffffff";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, [fetchBooks]);

  useEffect(() => {
    window.addEventListener("book-lists-updated", fetchBooks);
    return () => window.removeEventListener("book-lists-updated", fetchBooks);
  }, [fetchBooks]);

  const handleSwipeStart = (itemId, event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    swipeStateRef.current = {
      id: itemId,
      startX: touch.clientX,
      startY: touch.clientY,
      isSwiping: false
    };
    suppressOpenRef.current = false;
  };

  const handleSwipeMove = (itemId, event) => {
    const touch = event.touches?.[0];
    const swipeState = swipeStateRef.current;
    if (!touch || swipeState.id !== itemId) return;

    const deltaX = touch.clientX - swipeState.startX;
    const deltaY = touch.clientY - swipeState.startY;

    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
      return;
    }

    if (deltaX <= -24) {
      swipeState.isSwiping = true;
      suppressOpenRef.current = true;
      setSwipedBookId(itemId);
    } else if (deltaX >= 24 && swipedBookId === itemId) {
      swipeState.isSwiping = true;
      suppressOpenRef.current = true;
      setSwipedBookId(null);
    }
  };

  const handleSwipeEnd = (itemId) => {
    const swipeState = swipeStateRef.current;
    if (swipeState.id === itemId && swipeState.isSwiping) {
      window.setTimeout(() => {
        suppressOpenRef.current = false;
      }, 120);
    } else {
      suppressOpenRef.current = false;
    }

    swipeStateRef.current = {
      id: null,
      startX: 0,
      startY: 0,
      isSwiping: false
    };
  };

  const handleOpenBook = (bookId) => {
    if (suppressOpenRef.current) return;
    if (swipedBookId === bookId) {
      setSwipedBookId(null);
      return;
    }
    navigate(`/epub/${bookId}`);
  };

  const handleDeleteBookmark = async (bookId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const userId = user?.id || 0;
      const form = new FormData();
      form.append("uid", String(userId));
      form.append("epub_id", String(bookId));
      form.append("list", "bookmarks");
      const response = await fetch(`${apiUrl}/remove-book-from-list.php`, {
        method: "POST",
        credentials: "include",
        body: form
      });
      const data = await response.json();
      if (!response.ok || !data?.success) return;

      setBooks((current) => current.filter((book) => Number(book.id) !== Number(bookId)));
      setSwipedBookId(null);
      window.dispatchEvent(
        new CustomEvent("book-lists-updated", {
          detail: { epubId: bookId, list: "bookmarks" }
        })
      );
    } catch (error) {
      console.error("Failed to delete bookmark:", error);
    }
  };

  const name = (user?.displayName || "reader").toLowerCase();

  return (
    <div className="book-list-page app-shell-page">
      <div className="container-fluid px-0 pt-4 pb-5">
        <div className="book-list-page__titlebar bookmarks-page__titlebar px-3 px-md-4">
          <button type="button" className="book-list-page__back" onClick={() => navigate("/profile")}>
            <span className="material-symbols-rounded">arrow_back</span>
          </button>
          <div className="bookmarks-page__header">
            <h3 className="mb-0">
              Bookmarks <span className="book-list-page__count-accent fw-normal">{`\u2022 ${books.length} books`}</span>
            </h3>
          </div>
        </div>

        {loading ? (
          <p className="book-list-page__empty px-3 px-md-4 mt-4">Loading your bookmarks...</p>
        ) : books.length === 0 ? (
          <p className="book-list-page__empty px-3 px-md-4 mt-4">Books you bookmark with a review will show up here.</p>
        ) : (
          <div className="bookmarks-feed">
            {books.map((book, index) => (
              <div
                key={book.id}
                className={`bookmarks-feed__swipe${swipedBookId === book.id ? " bookmarks-feed__swipe--open" : ""}`}
                onTouchStart={(event) => handleSwipeStart(book.id, event)}
                onTouchMove={(event) => handleSwipeMove(book.id, event)}
                onTouchEnd={() => handleSwipeEnd(book.id)}
                onTouchCancel={() => handleSwipeEnd(book.id)}
              >
                <button
                  type="button"
                  className="bookmarks-feed__delete"
                  onClick={() => handleDeleteBookmark(book.id)}
                  aria-label="Delete bookmark"
                >
                  <span className="material-symbols-rounded">delete</span>
                </button>

                <button
                  type="button"
                  className={`bookmarks-feed__item text-start${index === 0 ? " bookmarks-feed__item--latest" : ""}`}
                  onClick={() => handleOpenBook(book.id)}
                >
                  {index === 0 && <span className="bookmarks-feed__latest">Latest</span>}

                  <div className="d-flex align-items-start gap-3 px-3 px-md-4">
                    <div className="bookmarks-feed__avatar">
                      {user?.profilePicture ? (
                        <img src={user.profilePicture} alt="" />
                      ) : (
                        <span>{(user?.displayName || "R").charAt(0).toUpperCase()}</span>
                      )}
                    </div>

                    <div className="bookmarks-feed__body">
                      <p className="bookmarks-feed__date mb-2">{formatDate(book.updated_at)}</p>
                      <p className="bookmarks-feed__headline mb-3">
                        <strong>{name}</strong>
                        {" bookmarked "}
                        <em>{book.title}</em>
                      </p>

                      <div className="bookmarks-feed__snippet">
                        {book.note_text || "No review attached yet."}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <AppNav />
    </div>
  );
}

export default BookmarksPage;
